const ARTICLES = [
  {
    slug: "security-guard-costs-england",
    title: "How Much Do Security Guards Cost in England? A 2026 Guide",
    description: "What security guards cost in England in 2026, what drives the hourly rate, and how to compare security company quotes without getting caught out.",
    category: "Pricing",
    date: "2026-07-14",
    readMins: 7,
    intro: [
      "It is the first question every buyer asks, and the one most security companies dodge: how much does a security guard actually cost? This guide explains how guarding rates are built in England, what a fair price looks like, and how to compare quotes on more than the headline number.",
    ],
    sections: [
      {
        h: "How security guard rates are built",
        paras: [
          "A guarding hourly rate is mostly labour: the officer's pay, holiday entitlement, pension, National Insurance, training and licensing. On top of that sit supervision, uniforms and equipment, insurance, command-centre support, and the provider's margin. When a quote looks suspiciously cheap, something in that stack is being cut — and it is usually the part that protects you.",
          "Rates also move with the role. A daytime static guard on a quiet site sits at the lower end; night work, door supervision, K9 units, close protection and multi-drop mobile patrols all command more because the licensing, risk and skill requirements are higher.",
        ],
      },
      {
        h: "What pushes the price up or down",
        paras: [
          "Location matters. London and the South East carry higher labour costs than the North East or the Midlands, and remote rural sites can cost more to cover than urban ones because of travel time. Shift pattern matters too: 24/7 cover needs more than four officers once rest days and holidays are accounted for.",
          "Contract length and notice period also shape the rate. A 12-month contract with a month's notice is cheaper to service than a two-week emergency deployment, and reputable providers price accordingly.",
        ],
      },
      {
        h: "Why the cheapest quote is usually the most expensive",
        paras: [
          "Illegal operators still exist: unlicensed guards, cash-in-hand pay, no insurance. If a provider cannot show SIA licences, BS 7858 vetting records and public liability cover, the saving is an illusion — and the liability for using an unlicensed guard can fall on you as the client.",
          "The better comparison is cost per verified hour. Ask every bidder how patrols are evidenced, how incidents are reported, and what happens when an officer calls in sick at 2am. The answers tell you more than the rate card.",
        ],
      },
      {
        h: "How to get an accurate quote",
        paras: [
          "Start with a proper risk assessment of your site: access points, existing measures, incident history and the value of what you are protecting. A provider who quotes without asking these questions is guessing — and guesses get renegotiated later.",
          "Xtreme Protection carries out free risk assessments across England and puts every quote in writing with duties, patrol routes and KPIs attached, so you can hold us to it. Call +44 2046 402840 or email admin@xtremeprotection.uk for same-day pricing.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is there a standard hourly rate for security guards in England?",
        a: "No single rate exists — pricing depends on the role, region, shift pattern and risk profile. Treat any provider quoting a flat national rate without seeing your site with caution.",
      },
      {
        q: "Are mobile patrols cheaper than a static guard?",
        a: "Usually, yes. Because a patrol officer covers multiple sites in a shift, you pay for visits rather than continuous presence. For lower-risk premises, patrols plus key holding can deliver most of the deterrent at a fraction of the cost.",
      },
      {
        q: "Do I pay more for nights and weekends?",
        a: "Often, yes — unsociable hours typically carry a premium. A transparent provider will show day, night and weekend rates separately in the written quote.",
      },
    ],
    relatedServices: ["manned-guarding", "mobile-security-patrols", "key-holding-alarm-response"],
    relatedCities: ["london", "manchester", "birmingham"],
  },
  {
    slug: "what-is-manned-guarding",
    title: "What Is Manned Guarding? A Plain-English Guide for Businesses",
    description: "Manned guarding explained: what static security guards actually do, when your site needs one, and what to check before you hire a guarding company.",
    category: "Guides",
    date: "2026-07-12",
    readMins: 6,
    intro: [
      "Manned guarding is the backbone of the UK security industry — a trained, licensed officer physically present on your premises. But what does a static guard actually do all shift, and when is a human presence worth the money over cameras alone? Here is the plain-English version.",
    ],
    sections: [
      {
        h: "What a manned guard actually does",
        paras: [
          "Far more than stand by a door. A professional static officer controls access and egress, verifies visitors and contractors, patrols the perimeter at set or random intervals, monitors alarms and CCTV, keeps a digital occurrence book, and acts as your first responder when something goes wrong.",
          "The deterrent effect is the quiet win. Most opportunist theft, vandalism and trespass simply moves on when a uniformed, alert officer is visibly present — a camera records a crime; a guard interrupts it.",
        ],
      },
      {
        h: "When your site needs manned guarding",
        paras: [
          "High-value stock or plant, repeated incidents, staff working alone at night, insurance requirements, or a duty of care to the public are all classic triggers. Construction sites, distribution centres, corporate receptions and licensed venues are the most common manned guarding environments in England.",
          "If your risk is lower, mobile patrols or monitored CCTV may be the proportionate answer — a good provider will tell you that rather than sell you hours you do not need.",
        ],
      },
      {
        h: "The legal baseline: SIA licensing",
        paras: [
          "In England it is a criminal offence to work as a security guard without a valid Security Industry Authority licence, and clients can be liable for using unlicensed staff. Before any contract starts, ask for licence numbers and check them on the SIA public register — it takes two minutes.",
          "Beyond the licence, look for BS 7858 vetting (the British Standard for screening security personnel), first-aid training and written assignment instructions specific to your site.",
        ],
      },
      {
        h: "Getting value from a guarding contract",
        paras: [
          "The difference between a guard and a guarding service is verification. NFC checkpoint tags, GPS-tracked patrols and time-stamped photographic reports turn 'we were there all night' into evidence you can audit, share with insurers and rely on in court if it ever comes to that.",
          "Xtreme Protection builds every manned guarding contract this way across England — free risk assessment first, written plan second, verified delivery from the first shift. Explore our manned guarding service or call +44 2046 402840.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is manned guarding the same as static guarding?",
        a: "In practice, yes — both terms describe a licensed officer assigned to a fixed site or post, as opposed to mobile patrols which move between multiple premises.",
      },
      {
        q: "How many guards does 24/7 cover need?",
        a: "Continuous single-officer cover typically requires four to five officers once shifts, rest days and holiday entitlement are accounted for.",
      },
      {
        q: "Can a guard also do reception or gatehouse duties?",
        a: "Yes — hybrid roles are common. Front-of-house, gatehouse and concierge duties can be written into the assignment instructions alongside security tasks.",
      },
    ],
    relatedServices: ["manned-guarding", "corporate-security", "concierge-security"],
    relatedCities: ["leeds", "liverpool", "bristol"],
  },
  {
    slug: "sia-licence-explained",
    title: "SIA Licences Explained: What Every Client Should Check",
    description: "What the SIA licence is, which security roles need one, and the two-minute check every client should run before hiring security staff in England.",
    category: "Compliance",
    date: "2026-07-10",
    readMins: 6,
    intro: [
      "The Security Industry Authority licence is the legal foundation of private security in England. If someone is guarding your premises, controlling your doors or responding to your alarms, the odds are the law requires them to hold one — and as the client, you have a duty to check.",
    ],
    sections: [
      {
        h: "What the SIA is and why it exists",
        paras: [
          "The Security Industry Authority is the government body that regulates the private security industry under the Private Security Industry Act 2001. Its job is to raise standards and keep people with serious criminal histories out of positions of trust.",
          "Licensing covers the main front-line roles: security guarding, door supervision, close protection, cash and valuables in transit, public space surveillance (CCTV), key holding and vehicle immobilising.",
        ],
      },
      {
        h: "Which roles need which licence",
        paras: [
          "Licences are role-specific. A door supervisor licence covers licensed premises work; a security guarding licence covers static and patrol duties; CCTV operators need a public space surveillance licence. A guard working outside their licence category is working illegally.",
          "Stewards at events are a common grey area: crowd management that involves searching, refusing entry or ejecting people is security work and needs a licence, whatever the badge says.",
        ],
      },
      {
        h: "The two-minute check every client should run",
        paras: [
          "Ask your provider for the name and licence number of every officer assigned to your site, then verify them on the SIA's public register. Check the licence type matches the role and that it is in date. Any hesitation from a provider at this request is a red flag.",
          "Go one step further and ask how officers are vetted. The industry benchmark is BS 7858 — background screening covering identity, employment history and criminal record checks.",
        ],
      },
      {
        h: "What good looks like beyond the licence",
        paras: [
          "A licence is the floor, not the ceiling. The providers worth hiring add first-aid training, conflict management, site-specific assignment instructions and supervised delivery with digital reporting — the difference between a person in a hi-vis and a professional security service.",
          "Every Xtreme Protection officer is SIA licensed, BS 7858 vetted and first-aid trained as standard. Call +44 2046 402840 to verify our credentials before you hire us — we would rather you checked.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is it illegal to hire an unlicensed security guard?",
        a: "Yes. Using unlicensed security staff is an offence under the Private Security Industry Act 2001, and clients who knowingly engage unlicensed guards expose themselves to prosecution and uninsured risk.",
      },
      {
        q: "How can I check an SIA licence?",
        a: "Use the SIA public register online — search by name or licence number and confirm the licence type and expiry date match the officer on your site.",
      },
      {
        q: "Do in-house security staff need SIA licences?",
        a: "Generally no — the licensing requirement applies to contracted security. However, door supervision at licensed premises requires a licence whether the staff are employed in-house or contracted.",
      },
    ],
    relatedServices: ["manned-guarding", "door-supervision", "cctv-monitoring"],
    relatedCities: ["newcastle-upon-tyne", "nottingham", "sheffield"],
  },
  {
    slug: "manned-guarding-vs-mobile-patrols",
    title: "Manned Guarding vs Mobile Patrols: Which Does Your Site Need?",
    description: "Static guard or mobile patrol? Compare cost, coverage and deterrent value to choose the right security model for your premises in England.",
    category: "Guides",
    date: "2026-07-09",
    readMins: 6,
    intro: [
      "It is the most common security buying decision a business makes: pay for a guard on site all night, or have a patrol vehicle check the premises several times instead? The honest answer depends on your risk — here is how to think it through.",
    ],
    sections: [
      {
        h: "The core difference",
        paras: [
          "Manned guarding gives you continuous presence: a licensed officer on your premises for the whole shift, able to intervene the moment something happens. Mobile patrols give you intermittent presence: a marked vehicle and officer attending at random or scheduled intervals, checking perimeters, doors, windows and plant.",
          "One is a wall; the other is a sweep. Neither is universally better — they answer different risk profiles at very different price points.",
        ],
      },
      {
        h: "When continuous guarding earns its cost",
        paras: [
          "If an incident at your site would cost more than the guarding — high-value stock, live construction plant, staff working alone, public-facing venues, or a current pattern of attacks — continuous presence is usually the rational choice. Guards intervene in real time; patrols discover after the fact.",
          "Guarding also wins where access control is the job: gatehouses, receptions and distribution hubs need a human making decisions all shift, not visiting it.",
        ],
      },
      {
        h: "Where mobile patrols are the smarter spend",
        paras: [
          "For lower-risk premises — closed offices, vacant buildings, storage yards, retail units in decent areas — several verified patrol visits per night plus key holding and alarm response deliver most of the deterrent at a fraction of the cost. Random timing matters: unpredictable visits are far harder to plan around than a fixed routine.",
          "Patrols scale well across portfolios too. Ten sites on one patrol route cost dramatically less to cover than ten static officers.",
        ],
      },
      {
        h: "The hybrid answer most sites end up with",
        paras: [
          "In practice, the strongest setups blend the models: static guarding during high-risk hours, patrols overnight, monitored CCTV filling the gaps, and key holding so an alarm activation sends a professional instead of a manager. The blend should come out of a risk assessment, not a sales call.",
          "Xtreme Protection designs hybrid packages across England with every patrol GPS-verified and photographically reported. Call +44 2046 402840 for a free assessment and an honest recommendation — including when we think you need less, not more.",
        ],
      },
    ],
    faqs: [
      {
        q: "How many patrol visits per night are enough?",
        a: "For most low-to-medium risk premises, two to four randomly timed visits per night is the norm. Higher-risk sites may need more frequent checks or continuous guarding instead.",
      },
      {
        q: "How do I know the patrol actually happened?",
        a: "Insist on GPS-tracked vehicles and NFC or QR checkpoint scans at your site, backed by time-stamped photographic reports after every visit.",
      },
      {
        q: "Can I switch between patrols and guarding later?",
        a: "Yes — a good provider will flex the model as your risk changes, scaling up for high-risk periods like shutdowns or stock deliveries and down when exposure drops.",
      },
    ],
    relatedServices: ["mobile-security-patrols", "manned-guarding", "key-holding-alarm-response"],
    relatedCities: ["oxford", "cambridge", "york"],
  },
  {
    slug: "construction-site-security-guide",
    title: "Construction Site Security: Stopping Plant, Tool and Fuel Theft",
    description: "How construction sites in England lose millions to theft each year, and the layered security measures that actually stop plant, tool and fuel crime.",
    category: "Sector Guides",
    date: "2026-07-08",
    readMins: 7,
    intro: [
      "Construction sites are among the most-crime-targeted workplaces in England. Open perimeters, high-value plant, tools and fuel, changing workforces and long quiet nights make them irresistible to organised thieves. Here is how to make your site the one they drive past.",
    ],
    sections: [
      {
        h: "What thieves actually target",
        paras: [
          "Plant and machinery lead the loss tables — excavators, dumpers, telehandlers and generators can vanish overnight on a low-loader. But the everyday bleed is tools, copper, cable and fuel: smaller thefts that rarely make reports yet quietly erase margin from a project.",
          "Timing is predictable. Most attacks happen between Friday evening and Monday morning, on holiday shutdowns, and in the dark months when sites stand empty from late afternoon.",
        ],
      },
      {
        h: "Layer one: the perimeter and the gate",
        paras: [
          "Everything starts with a defensible boundary: solid hoarding, anti-climb fencing, controlled gates and decent lighting. One access point, properly managed, beats three gaps patched with Heras panels. Deliveries and visitors should be booked, logged and escorted.",
          "Plant immobilisation is the cheap win that gets skipped — CESAR marking, immobilisers, fuel tank locks and storing small tools in a secured container turn an easy night into a noisy, slow one.",
        ],
      },
      {
        h: "Layer two: eyes that never blink",
        paras: [
          "Rapid-deployment CCTV towers with remote monitoring have transformed site security: detection zones around the perimeter, live audio warnings the moment someone breaches, and a verified alarm that dispatches response officers or police instead of a false-alarm shrug.",
          "On higher-risk sites, SIA-licensed guards — with or without K9 units — remain the strongest answer. A dog and handler can cover ground no camera sees and is a deterrent few intruders will test.",
        ],
      },
      {
        h: "Layer three: process and proof",
        paras: [
          "Most site theft involves inside knowledge, so process matters: plant registers, key control, workforce sign-in, and photographic records of plant and serial numbers. If the worst happens, that documentation is what gets an insurance claim paid and equipment recovered.",
          "Xtreme Protection builds layered construction site security across England — guards, K9, patrols and monitored towers scaled to each phase of the build. Call +44 2046 402840 for a free site survey.",
        ],
      },
    ],
    faqs: [
      {
        q: "Are CCTV towers enough on their own?",
        a: "For many smaller or medium-risk sites, yes — a monitored tower with audio challenge and a response contract is a strong standalone defence. High-value or repeatedly targeted sites should add guarding or K9 cover.",
      },
      {
        q: "When should security start on a new site?",
        a: "Earlier than most contractors think — from the first delivery of plant or materials, not from first fix. Sites are most exposed in the groundworks phase when perimeters are weakest.",
      },
      {
        q: "Do insurers require construction site security?",
        a: "Many policies specify minimum measures — fencing, lighting, immobilisation, sometimes guarding or monitored CCTV. Failing to meet them can void a claim, so check your policy wording before setting the security budget.",
      },
    ],
    relatedServices: ["construction-site-security", "k9-security-dog-handlers", "cctv-monitoring"],
    relatedCities: ["manchester", "birmingham", "leeds"],
  },
  {
    slug: "key-holding-alarm-response-guide",
    title: "Key Holding & Alarm Response: Ending the 3am Callout",
    description: "How key holding and alarm response works, why sending staff to night-time activations is a risk, and what a professional service includes.",
    category: "Guides",
    date: "2026-07-07",
    readMins: 5,
    intro: [
      "Every business with an alarm has the same unofficial job role nobody applied for: the keyholder who gets called at 3am. Professional key holding and alarm response exists to retire that role — here is how it works and what you should expect from it.",
    ],
    sections: [
      {
        h: "What key holding actually is",
        paras: [
          "A licensed security company holds a set of your premises keys in a secure control centre, under the BS 7984 standard for keyholding. When your alarm activates, a trained, SIA-licensed response officer is dispatched to investigate — not you, and not your staff.",
          "The same keys unlock a family of services: lock and unlock routines for opening and closing, access for contractors and deliveries, and scheduled property inspections.",
        ],
      },
      {
        h: "Why the DIY keyholder is a liability",
        paras: [
          "Most alarm activations are false — but nobody knows that before attending. Sending a lone, tired employee to a possible break-in is a genuine duty-of-care risk, and an employer who knows that and does it anyway is exposed if the worst happens.",
          "There is a commercial cost too: broken sleep, next-day absence, and the quiet resentment of whoever drew the short straw. Outsourced alarm response typically costs less per month than the disruption of a single bad callout.",
        ],
      },
      {
        h: "What happens when the alarm goes off",
        paras: [
          "The monitoring centre filters the activation and dispatches the nearest response officer. On arrival they check the perimeter, enter if needed, deal with the cause — from a blown door to an intruder — and liaise with police where required. Before leaving, the site is re-secured and the alarm reset.",
          "By morning you have a written report: time of activation, arrival time, findings, photographs and actions taken. That audit trail is what insurers and head offices want to see.",
        ],
      },
      {
        h: "Choosing a keyholding provider",
        paras: [
          "Check three things: SIA licensing (key holding is a licensable activity in England), BS 7984-compliant key management, and guaranteed response arrangements with GPS-verified attendance reporting. Ask what happens at 3am on a bank holiday — the answer should be boring.",
          "Xtreme Protection provides key holding and alarm response across England with verified attendance on every activation. Call +44 2046 402840 or email admin@xtremeprotection.uk.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is key holding a licensed activity?",
        a: "Yes — in England, holding keys for clients' premises as a service requires an SIA licence. Always verify your provider on the SIA public register.",
      },
      {
        q: "What is BS 7984?",
        a: "BS 7984 is the British Standard for keyholding and response services, covering secure key storage, key movement tracking and response procedures.",
      },
      {
        q: "Can you also lock and unlock our premises daily?",
        a: "Yes — lock and unlock services are a standard extension of key holding, removing the need for staff to open up or close down alone.",
      },
    ],
    relatedServices: ["key-holding-alarm-response", "mobile-security-patrols", "cctv-monitoring"],
    relatedCities: ["bristol", "liverpool", "sheffield"],
  },
  {
    slug: "retail-loss-prevention-guide",
    title: "Retail Loss Prevention: Practical Steps That Cut Shrinkage",
    description: "Retail shrinkage is rising across England. Practical loss prevention steps — from store guards to store detectives — that measurably reduce theft.",
    category: "Sector Guides",
    date: "2026-07-06",
    readMins: 6,
    intro: [
      "Shrinkage is the quiet tax on every retailer in England: shoplifting, staff theft, refund fraud and supplier errors combining to take a meaningful slice of turnover. The good news is that most of it is preventable with layered, boring, consistent controls.",
    ],
    sections: [
      {
        h: "Know where the losses come from",
        paras: [
          "External theft dominates headlines, but internal and process losses are a large share of shrinkage: till manipulation, sweethearting at the checkout, delivery discrepancies and admin errors. A loss prevention plan that only watches customers misses half the picture.",
          "Start with data. Incident logs, EPOS exceptions and CCTV review will show you the hotspots — the aisle, the shift, the entrance — and hotspots are where controls pay back fastest.",
        ],
      },
      {
        h: "The visible layer: uniformed store guards",
        paras: [
          "A uniformed, SIA-licensed officer at the entrance changes the economics for opportunist thieves: the store no longer looks like the easy option on the street. Guards also support staff during refusals and confrontations — the moments retail workers say they feel most exposed.",
          "Positioning matters. Greeter-style deployment near the entrance with a clear view of high-theft categories outperforms a guard lost at the back of the store.",
        ],
      },
      {
        h: "The invisible layer: store detectives and process",
        paras: [
          "Organised shoplifters know what a uniform looks like — which is where plain-clothes store detectives earn their keep, identifying repeat offenders and building the evidence packs that lead to prosecution or banning orders.",
          "Around the people, tighten the process: locked or attended high-value displays, till exception reporting, delivery checks, and CCTV that is actually monitored rather than just recorded.",
        ],
      },
      {
        h: "Making it pay",
        paras: [
          "Loss prevention is one of the few security spends with a measurable return: compare shrinkage percentage before and after deployment, quarter on quarter. Retailers who treat security as a measurable function rather than a grudge purchase routinely cut shrinkage by a third or more.",
          "Xtreme Protection provides retail guards, store detectives and shopping centre security across England. Call +44 2046 402840 for a free loss prevention review of your store.",
        ],
      },
    ],
    faqs: [
      {
        q: "Are store detectives legal?",
        a: "Yes — plain-clothes loss prevention is a standard, lawful practice. Detectives observing and detaining shoplifters work within the same legal framework as any security officer, and should be SIA licensed.",
      },
      {
        q: "Can security guards search customers?",
        a: "Only with consent. Guards can ask to look inside bags, but forced searches are unlawful — refusal can justify asking someone to leave, and detention is only appropriate where an offence is witnessed.",
      },
      {
        q: "Is a guard worth it for a small shop?",
        a: "For smaller stores, shared or part-time guarding, patrol visits and better process controls often deliver the return. A loss prevention review will show which control pays back first.",
      },
    ],
    relatedServices: ["retail-security", "cctv-monitoring", "manned-guarding"],
    relatedCities: ["london", "newcastle-upon-tyne", "nottingham"],
  },
  {
    slug: "event-security-planning-checklist",
    title: "Event Security in England: The Organiser's Planning Checklist",
    description: "Planning an event in England? A practical security checklist covering risk assessment, SIA licensing, crowd management and emergency planning.",
    category: "Sector Guides",
    date: "2026-07-05",
    readMins: 7,
    intro: [
      "Ask any experienced event organiser what keeps them awake and security will be near the top of the list. Whether it is a 500-person festival or a 50,000-capacity concert, the planning disciplines are the same — and they start weeks before the gates open.",
    ],
    sections: [
      {
        h: "Start with the risk assessment",
        paras: [
          "Every event security plan hangs off a proper risk assessment: expected attendance and audience profile, venue layout, alcohol, history of disorder, artist profile, weather exposure and the local crime picture. The assessment drives staffing numbers, deployment positions and the level of provision.",
          "Engage early with the local Safety Advisory Group, police and licensing authority where the event requires a licence. Their expectations — documented in the event management plan — are what your security provider must be able to deliver against.",
        ],
      },
      {
        h: "People: who you need and where",
        paras: [
          "SIA-licensed staff are required wherever security functions are performed: searching, refusing entry, ejections, guarding stages and assets. Stewards handle crowd information and wayfinding; mixing the roles without the licences is a compliance failure waiting for an inspection.",
          "The critical posts are the ones nobody notices when they work: entry search lanes, stage front-of-pit, egress routes, the control room and the response teams who deal with incidents before they become the story.",
        ],
      },
      {
        h: "Systems: control room, comms and counters",
        paras: [
          "A functioning event control room with a single radio network, CCTV oversight and one log of decisions is what separates managed events from lucky ones. Accurate capacity counting at every gate is a licensing and safety requirement, not an optional extra.",
          "Brief every member of staff — paid and volunteer — on the emergency procedures: show-stop protocol, evacuation routes, rendezvous points and who has authority to call what.",
        ],
      },
      {
        h: "After the last act",
        paras: [
          "The riskiest hour of many events is egress: crowds leaving, vehicles arriving, and staff wanting to go home. Hold security provision until the site is clear, then complete the debrief log while the detail is fresh — it is your defence and your improvement plan.",
          "Xtreme Protection plans and delivers event security across England, from community festivals to stadium shows. Call +44 2046 402840 to talk through your event with our planning team.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do event stewards need SIA licences?",
        a: "Stewards performing security functions — searching, refusing entry, ejecting attendees — must hold an SIA licence. Pure wayfinding and information stewarding does not, but the line is easy to cross, so plan staffing with a licensed provider.",
      },
      {
        q: "How many security staff does my event need?",
        a: "There is no lawful fixed ratio — numbers come from the risk assessment, audience profile, venue layout and activities. Your provider should evidence how they arrived at the figure.",
      },
      {
        q: "When should I book event security?",
        a: "As early as possible — for large events, months ahead, because licensing consultations and Safety Advisory Group engagement have their own lead times.",
      },
    ],
    relatedServices: ["event-security", "door-supervision", "k9-security-dog-handlers"],
    relatedCities: ["brighton-and-hove", "manchester", "bristol"],
  },
  {
    slug: "vacant-property-security-guide",
    title: "Vacant Property Security: Protecting Empty Buildings in England",
    description: "Empty buildings attract squatters, vandals and metal thieves — and void insurance conditions. How to keep a vacant property safe, legal and insurable.",
    category: "Sector Guides",
    date: "2026-07-03",
    readMins: 6,
    intro: [
      "An empty building is never really empty of risk. Squatters, vandals, metal thieves, arsonists and fly-tippers all treat void property as an invitation — and insurers treat it as a problem. Here is how landlords and agents across England keep voids protected, compliant and saleable.",
    ],
    sections: [
      {
        h: "What empty buildings attract",
        paras: [
          "The first wave is usually opportunistic: broken windows, graffiti, fly-tipping in the car park. Left unanswered, it escalates — copper and lead stripped from the fabric, squatters moving in, and the arson risk that turns a void into a write-off.",
          "Squatting in commercial property is a civil matter in England, which means slow, expensive possession proceedings. Prevention is dramatically cheaper than eviction.",
        ],
      },
      {
        h: "The insurance problem",
        paras: [
          "Most property policies change the moment a building becomes unoccupied: cover restricts, and conditions appear — typically regular documented inspections, secure physical measures, and utilities managed. Miss the inspection schedule and a claim can be declined entirely.",
          "This is why inspection reports matter as much as the inspection itself. A dated, photographed, time-stamped record of every visit is the document your insurer will ask for.",
        ],
      },
      {
        h: "The layered answer for voids",
        paras: [
          "Physical first: steel screens, boarded glazing, secured doors and cleared letterboxes. Then presence: scheduled mobile patrols and inspections on a documented rota. Then technology: battery-powered, rapidly deployed monitored CCTV towers that need no power or broadband on site.",
          "For large or high-risk voids, K9 patrol units and temporary guarding fill the gap between visits. The right mix depends on the building, the location and how long it will be empty.",
        ],
      },
      {
        h: "A service built for landlords and agents",
        paras: [
          "Xtreme Protection provides vacant property security across England for landlords, managing agents, receivers and public sector estates: inspections, patrols, monitored towers and physical security, all with insurer-friendly reporting as standard.",
          "Call +44 2046 402840 or email admin@xtremeprotection.uk for a free void risk assessment — most properties can be secured within days.",
        ],
      },
    ],
    faqs: [
      {
        q: "How often should a vacant property be inspected?",
        a: "Weekly inspections are the most common insurance condition for unoccupied buildings, though some policies require more frequent checks. Always confirm the exact wording of your policy.",
      },
      {
        q: "Can CCTV work on a site with no power or internet?",
        a: "Yes — rapid-deployment towers are self-powered and connect over mobile networks, making them ideal for voids where utilities are disconnected.",
      },
      {
        q: "Is it legal to remove squatters from a commercial building?",
        a: "Commercial squatting is a civil matter in England and usually requires a possession order or interim possession order. Prevention through physical security and documented inspections is far cheaper than eviction proceedings.",
      },
    ],
    relatedServices: ["vacant-property-security", "cctv-monitoring", "mobile-security-patrols"],
    relatedCities: ["birmingham", "leeds", "london"],
  },
  {
    slug: "bs7858-vetting-explained",
    title: "BS 7858 Vetting Explained: Why Security Screening Matters",
    description: "What BS 7858 vetting covers, why it matters when hiring security staff, and the screening questions to ask any security provider in England.",
    category: "Compliance",
    date: "2026-07-01",
    readMins: 5,
    intro: [
      "When you hire a security officer, you hand a stranger the keys to everything you own. BS 7858 exists to make sure they are not a stranger at all — it is the British Standard for screening people working in security, and it is what separates professional providers from a man and a hi-vis.",
    ],
    sections: [
      {
        h: "What BS 7858 actually covers",
        paras: [
          "The standard sets out a thorough screening process: identity verification, right-to-work checks, a five-year employment and education history with gaps explained, criminal record checking appropriate to the role, address history verification and character references.",
          "It is designed for exactly the risk you care about: people placed alone, at night, inside your premises, with access to your assets, your systems and your people.",
        ],
      },
      {
        h: "How it sits alongside SIA licensing",
        paras: [
          "The SIA licence confirms an officer has passed the Authority's own criminality checks and training for their role. BS 7858 goes deeper into employment history and character. Serious providers require both: the licence as the legal baseline, and the vetting as the quality standard.",
          "Be wary of any provider who treats the licence as sufficient. The SIA check is a snapshot; BS 7858 is a file.",
        ],
      },
      {
        h: "What to ask your security provider",
        paras: [
          "Three questions expose everything: Are all your officers vetted to BS 7858? Can I see evidence of vetting for the officers assigned to my contract? And do you re-screen when officers move roles or after long gaps? Evasive answers are your answer.",
          "For sensitive environments — schools, healthcare, finance — ask about enhanced screening and DBS checks on top of the standard.",
        ],
      },
      {
        h: "Our standard, on the record",
        paras: [
          "Every Xtreme Protection officer is SIA licensed, vetted to BS 7858, first-aid trained and briefed under written, site-specific assignment instructions before deployment. We will show you the paperwork — that is the point of having it.",
          "Call +44 2046 402840 or email admin@xtremeprotection.uk to talk through screening for your contract.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is BS 7858 a legal requirement?",
        a: "It is a British Standard rather than a statute, but it is the recognised industry benchmark — and many insurers, public sector frameworks and corporate procurement teams make it a contractual requirement.",
      },
      {
        q: "How long does BS 7858 screening take?",
        a: "Typically two to four weeks depending on how quickly references respond, which is why reputable providers maintain a pool of pre-vetted officers ready to deploy.",
      },
      {
        q: "Does vetting expire?",
        a: "Screening is refreshed when officers change roles or employers, and after significant breaks in employment. Continuous employment with one vetted provider keeps the file current.",
      },
    ],
    relatedServices: ["corporate-security", "healthcare-security", "student-campus-security"],
    relatedCities: ["sheffield", "liverpool", "oxford"],
  },
];

import BATCH2 from "./articles2";
import BATCH3 from "./articles3";

const ALL_ARTICLES = [...ARTICLES, ...BATCH2, ...BATCH3];

export default ALL_ARTICLES;

export const getArticle = (slug) => ALL_ARTICLES.find((a) => a.slug === slug);

export const relatedArticles = (article, n = 3) =>
  ALL_ARTICLES.filter((a) => a.slug !== article.slug)
    .sort(
      (x, y) =>
        y.relatedServices.filter((s) => article.relatedServices.includes(s)).length -
        x.relatedServices.filter((s) => article.relatedServices.includes(s)).length
    )
    .slice(0, n);

export const articlesForService = (serviceSlug, n = 2) => {
  const direct = ALL_ARTICLES.filter((a) => a.relatedServices.includes(serviceSlug));
  return (direct.length ? direct : ALL_ARTICLES).slice(0, n);
};

export const articlesForCity = (citySlug, n = 2) => {
  const direct = ALL_ARTICLES.filter((a) => a.relatedCities.includes(citySlug));
  if (direct.length) return direct.slice(0, n);
  const seed = [...citySlug].reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 7);
  const idx = Math.abs(seed) % ALL_ARTICLES.length;
  return [ALL_ARTICLES[idx], ALL_ARTICLES[(idx + 3) % ALL_ARTICLES.length]];
};
